'use client';

import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';

// @ts-expect-error - no type definitions for @analytics/segment
import SegmentPlugin from '@analytics/segment';
import Analytics, { AnalyticsInstance, AnalyticsPlugin } from 'analytics';

const getEnvironment = (): string => process.env.ENVIRONMENT || 'development';
const environment = getEnvironment();

const searchPluginFunction = ({ payload }: { payload: object }): void => {
  const event = { ...payload, environment, application: 'CriblCloudPortal' };

  if (environment === 'development' || process.env.NODE_ENV === 'test') {
    console.debug('event sent', event);

    return;
  }

  fetch('/api/experience', {
    method: 'POST',
    body: JSON.stringify(event),
    headers: { 'Content-Type': 'application/json' },
  });
};

const SearchPlugin: AnalyticsPlugin = {
  name: 'cribl-search-integration',
  page: searchPluginFunction,
  track: searchPluginFunction,
  identify: searchPluginFunction,
  loaded: () => true,
};

let analytics: AnalyticsInstance;

const initializeAnalytics = () => {
  analytics = Analytics({
    app: 'maestro-ui',
    debug: true,
    plugins: [
      process.env.CUSTOM_ANALYTICS_ENABLED === 'true' && SearchPlugin,
      process.env.SEGMENT_ENABLED === 'true' && process.env.SEGMENT_UI_SOURCE_KEY
        ? SegmentPlugin({
            writeKey: process.env.SEGMENT_UI_SOURCE_KEY,
          })
        : null,
    ].filter(Boolean),
  });
};

type TrackFunction<T extends string> = (props: { event: T; payload?: object }) => void;

type IdentifyFunction = (props: { userId?: string }) => void;

type PageFunction = () => void;

export type AnalyticsHook<TEvent extends string> = {
  track: TrackFunction<TEvent>;
  identify: IdentifyFunction;
  page: PageFunction;
};

type Props = {
  organizationId?: string;
  module?: string;
};

export const useAnalytics = <TEvent extends string>(props?: Props): AnalyticsHook<TEvent> => {
  if (!analytics) {
    initializeAnalytics();
  }

  const { user } = useCommonAuthProvider();

  const context = {
    organizationId: props?.organizationId || null,
    email: user?.email || null,
    module: props?.module || null,
  };

  const track: TrackFunction<TEvent> = ({ event, payload }) => {
    analytics.track(event, { context, payload: payload || null });
  };

  const identify: IdentifyFunction = ({ userId }) => {
    analytics.identify(userId || '', { context });
  };

  const page: PageFunction = () => {
    analytics.page({ context });
  };

  return {
    identify,
    track,
    page,
  };
};
